import type { Expand } from "@pdfking/tfunc"
import {
  Identity,
  IsArray,
  IsFunction,
  IsNull,
  Items,
  RecordMerge,
  Void,
} from "@pdfking/tfunc"
import { RunAllWith } from "../utils/Lifecycle"
import { DefineProperty } from "../utils/Properties"

const ToArray = <T>(...args: (T | T[] | undefined)[]): T[] =>
  args.flatMap(v => (IsNull(v) ? [] : IsArray(v) ? v : [v]))

// Strip index signature, keep only known keys
type KnownKeys<T> = {
  [K in keyof T as string extends K ? never : K]: T[K]
}

// Constraints for generic parameters
type DataConstraint = Record<string, any>
type PropsConstraint = Record<string, any>
type MethodsConstraint = Record<string, (...args: any[]) => any>
type ComputedConstraint = Record<
  string,
  (() => any) | { get: () => any; set: (v: any) => void }
>

// Extract return type from computed definition
type ComputedReturnType<C> = C extends () => infer R
  ? R
  : C extends { get: () => infer R }
    ? R
    : never

// Map computed definitions to their value types
type ComputedValues<C> = {
  [K in keyof C]: ComputedReturnType<C[K]>
}

type Constructors = {
  set_data: (instance: any, key: string, value: any) => void
  set_methods: (
    instance: any,
    key: string,
    value: (...args: any[]) => any
  ) => void
  set_computed: (
    instance: any,
    key: string,
    get: () => any,
    set: (v: any) => void
  ) => void
  init: (instance: any) => any
  effect: (fn: () => void) => void
}

// Lifecycle hook types
type LifecycleHook = (() => void) | (() => void)[]
type MountedHook = ((el: HTMLElement) => void) | ((el: HTMLElement) => void)[]

// Default constructors for each property type
const defaultConstructors: Constructors = {
  effect: Void,
  init: Identity,
  set_computed: DefineProperty,
  set_data: (instance, key, value) => (instance[key] = value),
  set_methods: (instance, key, value) => (instance[key] = value),
}

// Base shape for mixin-like objects
type MixinLike = {
  props?: any
  data?: any
  methods?: any
  computed?: any
  watch?: any
  init?: any
  mounted?: any
  unmounted?: any
  render?: any
}

// Helper to merge props/data/methods/computed separately
type MergeAllProps<T extends readonly MixinLike[]> = T extends readonly [
  infer First extends MixinLike,
  ...infer Rest extends readonly MixinLike[],
]
  ? Expand<
      Omit<First["props"], keyof MergeAllProps<Rest>> & MergeAllProps<Rest>
    >
  : {}

type MergeAllData<T extends readonly MixinLike[]> = T extends readonly [
  infer First extends MixinLike,
  ...infer Rest extends readonly MixinLike[],
]
  ? Expand<Omit<First["data"], keyof MergeAllData<Rest>> & MergeAllData<Rest>>
  : {}

type MergeAllMethods<T extends readonly MixinLike[]> = T extends readonly [
  infer First extends MixinLike,
  ...infer Rest extends readonly MixinLike[],
]
  ? Expand<
      Omit<First["methods"], keyof MergeAllMethods<Rest>> &
        MergeAllMethods<Rest>
    >
  : {}

type MergeAllComputed<T extends readonly MixinLike[]> = T extends readonly [
  infer First extends MixinLike,
  ...infer Rest extends readonly MixinLike[],
]
  ? Expand<
      Omit<First["computed"], keyof MergeAllComputed<Rest>> &
        MergeAllComputed<Rest>
    >
  : {}

// Helper to extract merged this type directly from parts
type MergedThis<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
> = Expand<
  Readonly<MergeAllProps<Mixins> & Props> &
    (MergeAllData<Mixins> & Data) &
    KnownKeys<MergeAllMethods<Mixins> & Methods> &
    ComputedValues<MergeAllComputed<Mixins> & Computed>
>

// Watch handler type - keys must be from data or computed
type WatchHandler<T> = (newVal: T, oldVal: T) => void

// All watchable keys (props + data + computed values)
type WatchableKeys<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Computed extends ComputedConstraint,
> = Expand<
  (MergeAllProps<Mixins> & Props) &
    (MergeAllData<Mixins> & Data) &
    ComputedValues<MergeAllComputed<Mixins> & Computed>
>

// Mixin object returned by CreateMixin
type Mixin<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
> = Expand<{
  props: Expand<MergeAllProps<Mixins> & Props>
  data: Expand<MergeAllData<Mixins> & Data>
  methods: Expand<MergeAllMethods<Mixins> & Methods>
  computed: Expand<MergeAllComputed<Mixins> & Computed>
  watch: {
    [K in keyof WatchableKeys<Mixins, Props, Data, Computed>]?: WatchHandler<
      WatchableKeys<Mixins, Props, Data, Computed>[K]
    >
  }
  init: (() => void)[]
  mounted: ((el: HTMLElement) => void)[]
  unmounted: ((el: HTMLElement) => void)[]
  render?: ((...args: any[]) => any) | undefined
}>

// Instance type returned by Init
type ComponentInstance<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
> = MergedThis<Mixins, Props, Data, Methods, Computed>

// Full this type for component config
type FullThis<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
> = Expand<
  Readonly<MergeAllProps<Mixins> & Props> &
    (MergeAllData<Mixins> & Data) &
    KnownKeys<MergeAllMethods<Mixins> & Methods> &
    ComputedValues<MergeAllComputed<Mixins> & Computed>
>

// Config input for CreateMixin
type MixinConfig<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
> = {
  mixins?: Mixins
  props?: Props
  data?: Data
  methods?: Methods
  computed?: Computed
  watch?: Record<string, (newVal: any, oldVal: any) => void>
  init?: LifecycleHook
  mounted?: MountedHook
  unmounted?: MountedHook
  render?: ((...args: any[]) => any) | undefined
} & ThisType<FullThis<Mixins, Props, Data, Methods, Computed>>

// Full component config = mixin config + render
type ComponentConfig<
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
  Element,
> = MixinConfig<Mixins, Props, Data, Methods, Computed> & {
  render?: (children?: Element[]) => Element | undefined
}

// Type for framework-specific component factory
type FrameworkComponentFactory<Element> = (
  init: (props?: any) => any,
  config: {
    render: (...args: any[]) => Element
    mounted: ((el: HTMLElement) => void)[]
    unmounted: ((el: HTMLElement) => void)[]
  }
) => (props?: any) => Element

export type MountFunction<Element> = (
  renderFn: () => Element,
  el: HTMLElement
) => ChildNode

// --- CreateMixin ---
// Merges mixins + config into a pure state mixin object (no lifecycle, no constructors)

export const CreateMixin = <
  const Mixins extends readonly MixinLike[] = [],
  Props extends PropsConstraint = {},
  Data extends DataConstraint = {},
  Methods extends MethodsConstraint = {},
  Computed extends ComputedConstraint = {},
>(
  config: MixinConfig<Mixins, Props, Data, Methods, Computed>
): Mixin<Mixins, Props, Data, Methods, Computed> =>
  [{}, ...(config.mixins ?? []), config].reduce((acc, m) => ({
    computed: { ...acc.computed, ...m.computed },
    data: { ...acc.data, ...m.data },
    init: ToArray(acc.init, m.init),
    methods: { ...acc.methods, ...m.methods },
    mounted: ToArray(acc.mounted, m.mounted),
    props: { ...acc.props, ...m.props },
    render: m.render ?? acc.render,
    unmounted: ToArray(acc.unmounted, m.unmounted),
    watch: { ...acc.watch, ...m.watch },
  })) as Mixin<Mixins, Props, Data, Methods, Computed>

// --- initWithMixin ---
// Creates a reactive instance from an already-resolved Mixin + constructors

const initWithMixin = <
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
>(
  mixin: Mixin<Mixins, Props, Data, Methods, Computed>,
  constructors: Constructors,
  partial?: Partial<MergeAllProps<Mixins> & Props>
): ComponentInstance<Mixins, Props, Data, Methods, Computed> => {
  const {
    set_data,
    set_methods,
    set_computed,
    init: initFn,
    effect,
  } = constructors

  let instance: any = { _mixin: mixin }

  // Props + Data — set up as reactive fields
  for (const [key, value] of Items(
    RecordMerge(mixin.props, mixin.data, partial || {})
  )) {
    set_data(instance, key, value)
  }

  for (const [key, value] of Items(mixin.methods as MethodsConstraint)) {
    set_methods(instance, key, value.bind(instance))
  }

  for (const [key, value] of Items(mixin.computed as ComputedConstraint)) {
    const def = IsFunction(value)
      ? { get: value, set: (v: any) => set_data(instance, key, v) }
      : value
    set_computed(instance, key, def.get.bind(instance), def.set.bind(instance))
  }

  instance = initFn(instance) ?? instance
  RunAllWith(mixin.init, instance)

  for (const [key, handler] of Items(mixin.watch)) {
    let oldVal = instance[key]
    effect(() => {
      const newVal = instance[key]
      if (newVal !== oldVal) {
        ;(handler as any).call(instance, newVal, oldVal)
        oldVal = newVal
      }
    })
  }

  return instance
}

// --- Init ---
// Public API: takes user config, resolves mixins, creates reactive instance

const Init = <
  Mixins extends readonly MixinLike[],
  Props extends PropsConstraint,
  Data extends DataConstraint,
  Methods extends MethodsConstraint,
  Computed extends ComputedConstraint,
>(
  config: MixinConfig<Mixins, Props, Data, Methods, Computed>,
  constructors: Constructors,
  partial?: Partial<MergeAllProps<Mixins> & Props>
): ComponentInstance<Mixins, Props, Data, Methods, Computed> =>
  initWithMixin<Mixins, Props, Data, Methods, Computed>(
    CreateMixin(config),
    constructors,
    partial
  )

// --- CreateRenderFunction ---
// Takes a component config + framework component factory, returns a JSX component function

const CreateRenderFunction = <
  const Mixins extends readonly MixinLike[] = [],
  Props extends PropsConstraint = {},
  Data extends DataConstraint = {},
  Methods extends MethodsConstraint = {},
  Computed extends ComputedConstraint = {},
  Element = unknown,
>(
  config: ComponentConfig<Mixins, Props, Data, Methods, Computed, Element>,
  constructors: Constructors,
  componentFactory: FrameworkComponentFactory<Element>,
  defaultRender: (...args: any[]) => Element
): ((
  props?: Partial<MergeAllProps<Mixins> & Props> & { children?: any }
) => Element) => {
  const mixin = CreateMixin(config)
  return componentFactory(
    (partial?) => initWithMixin(mixin, constructors, partial),
    {
      mounted: mixin.mounted,
      render: config.render ?? mixin.render ?? defaultRender,
      unmounted: mixin.unmounted,
    }
  )
}

// --- CreateReactivityFunctions ---
// Takes all backend-specific pieces, returns bound functions with constructors in closure

export type InitFunction = <
  const Mixins extends readonly MixinLike[] = [],
  Props extends PropsConstraint = {},
  Data extends DataConstraint = {},
  Methods extends MethodsConstraint = {},
  Computed extends ComputedConstraint = {},
>(
  mixin: MixinConfig<Mixins, Props, Data, Methods, Computed>,
  partial?: Partial<MergeAllProps<Mixins> & Props>
) => ComponentInstance<Mixins, Props, Data, Methods, Computed>

export type CreateRenderFunctionType<Element> = <
  const Mixins extends readonly MixinLike[] = [],
  Props extends PropsConstraint = {},
  Data extends DataConstraint = {},
  Methods extends MethodsConstraint = {},
  Computed extends ComputedConstraint = {},
>(
  config: ComponentConfig<Mixins, Props, Data, Methods, Computed, Element>
) => (
  props?: Partial<MergeAllProps<Mixins> & Props> & { children?: any }
) => Element

export type ReactivityFunctions<Element> = {
  Init: InitFunction
  CreateRenderFunction: CreateRenderFunctionType<Element>
  Mount: MountFunction<Element>
}

export const CreateReactivityFunctions = <Element>(
  constructors: Partial<Constructors>,
  mountFn: MountFunction<Element>,
  componentFactory: FrameworkComponentFactory<Element>,
  defaultRender: (...args: any[]) => Element
): ReactivityFunctions<Element> => {
  const resolvedConstructors = { ...defaultConstructors, ...constructors }
  return {
    CreateRenderFunction: (config: any) =>
      CreateRenderFunction(
        config,
        resolvedConstructors,
        componentFactory,
        defaultRender
      ),
    Init: (m: any, partial?: any) => Init(m, resolvedConstructors, partial),
    Mount: (renderFn, el) => mountFn(renderFn, el),
  }
}

export type NextFrame = (fn: () => void) => void
